import { formatMoney } from '@/lib/money'
import { cn } from '@/lib/utils'
import type { CachedPosProduct } from './offline/types'

type Props = {
  product: CachedPosProduct
  currency: string
  inCartQty: number
  lowStockThreshold: number
  justAdded: boolean
  onAdd: () => void
}

export function PosProductCard({
  product,
  currency,
  inCartQty,
  lowStockThreshold,
  justAdded,
  onAdd,
}: Props) {
  const stockRaw = product.stockQuantity
  const stockTracked = stockRaw != null && Number.isFinite(Number(stockRaw))
  const stockNum = stockTracked ? Number(stockRaw) : null
  const outOfStock = stockTracked && stockNum != null && stockNum <= 0
  const atStockLimit =
    stockTracked && stockNum != null && inCartQty >= stockNum
  const disabled = outOfStock || atStockLimit
  const showLowStock =
    stockTracked &&
    stockNum != null &&
    stockNum > 0 &&
    stockNum <= lowStockThreshold
  const imageUrl =
    Array.isArray(product.images) && product.images.length > 0 ? product.images[0] : null

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onAdd}
      aria-label={
        outOfStock
          ? `${product.name} — out of stock`
          : atStockLimit
            ? `${product.name} — no more stock available`
            : `Add ${product.name} to sale`
      }
      className={cn(
        'group border-border bg-card relative flex flex-col overflow-hidden rounded-2xl border text-left shadow-sm transition',
        'focus-visible:ring-ring focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none',
        'active:scale-[0.98] motion-reduce:active:scale-100',
        disabled
          ? 'cursor-not-allowed opacity-55'
          : 'hover:border-primary/35 hover:shadow-md',
        justAdded && 'border-primary/50 ring-primary/30 ring-2',
      )}
    >
      <div className="bg-muted/40 relative aspect-square w-full overflow-hidden">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt=""
            className="h-full w-full object-cover"
            loading="lazy"
            decoding="async"
          />
        ) : (
          <div
            className="text-muted-foreground flex h-full w-full items-center justify-center text-xs font-medium"
            aria-hidden
          >
            No image
          </div>
        )}
        {showLowStock ? (
          <span className="absolute top-2 left-2 rounded-md bg-red-600 px-1.5 py-0.5 text-[10px] font-bold text-white tabular-nums shadow-sm">
            {stockNum} left
          </span>
        ) : null}
        {outOfStock ? (
          <span className="absolute top-2 left-2 rounded-md bg-foreground/80 px-1.5 py-0.5 text-[10px] font-bold text-background shadow-sm">
            Out of stock
          </span>
        ) : null}
        {inCartQty > 0 ? (
          <span className="bg-primary text-primary-foreground absolute top-2 right-2 flex min-h-6 min-w-6 items-center justify-center rounded-full px-1.5 text-xs font-bold tabular-nums shadow-sm">
            {inCartQty > 99 ? '99+' : inCartQty}
          </span>
        ) : null}
      </div>
      <div className="flex min-h-[4.5rem] flex-1 flex-col gap-0.5 p-3">
        <p className="text-foreground line-clamp-2 text-sm font-semibold leading-snug">{product.name}</p>
        <p className="text-foreground mt-auto text-sm font-semibold tabular-nums">
          {formatMoney(Number(product.price), currency)}
        </p>
        {atStockLimit && !outOfStock ? (
          <p className="text-muted-foreground text-[11px]">Max in cart</p>
        ) : null}
      </div>
    </button>
  )
}
